import { DuplicateChange } from "@/store/zustand/changeLedgerTypes";
import { NavTreeNode } from "@/utils/types";
import { TempoElement } from "@/utils/tempoElement";
import {
  NavTreeUpdates,
  fixUniquePathsForAllChildren,
} from "./navTreeLookupUtils";
import { getCodebaseIdFromNode } from "@/components/Canvas/utils/utils";
import { getElementsForCodebaseId } from "./domUtils";
import { updateCodebaseId, ApplyInstantUpdateProps } from "./utils";

export const applyDuplicateJsx = (props: ApplyInstantUpdateProps) => {
  const { iframeDocument, storyboardId, persistentData } = props;
  const change = props.change as DuplicateChange;
  change.changeFields.codebaseIdsToDuplicate.forEach(
    (codebaseIdToDuplicate: string) => {
      const elementsToDuplicate = getElementsForCodebaseId(
        iframeDocument,
        codebaseIdToDuplicate,
      );

      if (!elementsToDuplicate.length) {
        return;
      }

      persistentData.storyboardsAppliedTo.add(storyboardId);

      // For each item that is removed, save the inner HTML in case it gets deleted and we want to undo
      elementsToDuplicate.forEach((item) => {
        const clonedNode = item.cloneNode(true) as HTMLElement;
        const newCodebaseId = updateCodebaseId(
          codebaseIdToDuplicate,
          change.changeFields.codebaseIdHashingKey,
        );
        clonedNode.setAttribute("data-tempoelementid", newCodebaseId);

        // Update codebase ids of all children
        const children = Array.from(clonedNode.children);
        while (children.length) {
          const child = children.pop();
          if (!child) {
            continue;
          }

          const codebaseId = getCodebaseIdFromNode(child as HTMLElement);
          if (!codebaseId) {
            continue;
          }

          const childNewCodebaseId = updateCodebaseId(
            codebaseId,
            change.changeFields.codebaseIdHashingKey,
          );

          child.setAttribute("data-tempoelementid", childNewCodebaseId);

          children.push(...Array.from(child.children));
        }

        // Add the clones node right after the found node
        item.insertAdjacentElement("afterend", clonedNode);
      });
    },
  );
};

export const undoDuplicateJsx = (props: ApplyInstantUpdateProps) => {
  const { iframeDocument, storyboardId } = props;
  const change = props.change as DuplicateChange;
  // Remove all duplicate placeholder nodes
  change.changeFields.codebaseIdsToDuplicate.forEach(
    (codebaseIdToDuplicate: string) => {
      const duplicateCodebaseId = updateCodebaseId(
        codebaseIdToDuplicate,
        change.changeFields.codebaseIdHashingKey,
      );

      getElementsForCodebaseId(iframeDocument, duplicateCodebaseId).forEach(
        (element) => {
          element.remove();
        },
      );
    },
  );
};

// Update nav tree for DUPLICATE_JSX change
export const applyDuplicateJsxToNavTree = (
  props: ApplyInstantUpdateProps,
): void => {
  const { navTreeUpdates, storyboardId } = props;
  const change = props.change as DuplicateChange;
  const codebaseIdsToDuplicate = change.changeFields.codebaseIdsToDuplicate;
  if (codebaseIdsToDuplicate.length === 0) return;

  const parentsToFix = new Set<NavTreeNode>();

  codebaseIdsToDuplicate.forEach((codebaseIdToDuplicate: string) => {
    const nodesToDuplicate =
      navTreeUpdates.codebaseIdLookup[codebaseIdToDuplicate] || [];

    nodesToDuplicate.forEach((node: NavTreeNode) => {
      const parent = node.parent;
      if (!parent) return;

      // Clone the node and all its children
      const clonedNode = cloneNavTreeNode(
        node,
        parent,
        storyboardId,
        change.changeFields.codebaseIdHashingKey,
      );

      // Find the index of the original node
      const index = parent.children?.indexOf(node) ?? -1;
      if (index === -1) return;

      // Insert the cloned node right after the original
      parent.children.splice(index + 1, 0, clonedNode);

      // Track parent for fixing unique paths
      parentsToFix.add(parent);
    });
  });

  // Fix unique paths for all affected parents
  parentsToFix.forEach((parent) => fixUniquePathsForAllChildren(parent));
};

// Helper function to clone a nav tree node and update codebase IDs
function cloneNavTreeNode(
  node: NavTreeNode,
  parent: NavTreeNode,
  storyboardId: string,
  codebaseIdHashingKey: string,
): NavTreeNode {
  const clonedCodebaseId = updateCodebaseId(
    node.tempoElement.codebaseId,
    codebaseIdHashingKey,
  );

  const clonedNode: NavTreeNode = {
    parent,
    children: [],
    tempoElement: new TempoElement(
      clonedCodebaseId,
      storyboardId,
      node.tempoElement.uniquePath, // Will be fixed by fixUniquePathsForAllChildren
    ),
    name: node.name,
    elementTagName: node.elementTagName,
    isComponent: node.isComponent,
    level: node.level,
    classList: node.classList ? [...node.classList] : undefined,
    props: node.props ? { ...node.props } : undefined,
    literalChildren: node.literalChildren
      ? [...node.literalChildren]
      : undefined,
    domUniquePath: node.domUniquePath, // Will be fixed by fixUniquePathsForAllChildren
    domUniquePathForComponent: node.domUniquePathForComponent,
  };

  // Clone all children recursively
  if (node.children) {
    clonedNode.children = node.children.map((child) =>
      cloneNavTreeNode(child, clonedNode, storyboardId, codebaseIdHashingKey),
    );
  }

  return clonedNode;
}

// Undo nav tree changes for DUPLICATE_JSX change
export const undoDuplicateJsxToNavTree = (
  props: ApplyInstantUpdateProps,
): void => {
  const { navTreeUpdates } = props;
  const change = props.change as DuplicateChange;
  const codebaseIdsToDuplicate = change.changeFields.codebaseIdsToDuplicate;
  if (codebaseIdsToDuplicate.length === 0) return;

  const parentsToFix = new Set<NavTreeNode>();

  // Remove duplicated nodes by finding them using their placeholder codebase IDs
  codebaseIdsToDuplicate.forEach((originalCodebaseId: string) => {
    const duplicatePlaceholderCodebaseId = updateCodebaseId(
      originalCodebaseId,
      change.changeFields.codebaseIdHashingKey,
    );
    const duplicatedNodes =
      navTreeUpdates.codebaseIdLookup[duplicatePlaceholderCodebaseId] || [];

    duplicatedNodes.forEach((duplicatedNode) => {
      const parent = duplicatedNode.parent;
      if (!parent) return;

      // Remove the duplicated node from parent's children
      const index = parent.children?.indexOf(duplicatedNode) ?? -1;
      if (index !== -1) {
        parent.children.splice(index, 1);
        parentsToFix.add(parent);
      }
    });
  });

  // Fix unique paths for all affected parents
  parentsToFix.forEach((parent) => fixUniquePathsForAllChildren(parent));
};

// Consolidated apply function that handles both DOM and nav tree
export const applyDuplicateJsxChange = (
  props: ApplyInstantUpdateProps,
): void => {
  // Apply DOM changes
  applyDuplicateJsx(props);

  // Apply nav tree changes
  if (!props.skipUpdateNavTree) {
    applyDuplicateJsxToNavTree(props);
  }
};

// Consolidated undo function that handles both DOM and nav tree
export const undoDuplicateJsxChange = (
  props: ApplyInstantUpdateProps,
): void => {
  // Undo DOM changes
  undoDuplicateJsx(props);

  // Undo nav tree changes
  if (!props.skipUpdateNavTree) {
    undoDuplicateJsxToNavTree(props);
  }
};
